import type { ApolloClient } from '@apollo/client';
import { SAVE_COLLECTION_ITEM } from '../../graphql/mutations';
import { sha256Hex, uploadAsset } from '../media/uploadAsset';
import { QueuedItem, listQueue, removeItem, updateItem } from './queue';

/**
 * Draining the capture queue: images into the media library, then one card
 * upsert.
 *
 * Per image:  sha256 → digest probe → POST /api/upload/asset
 * Then once:  saveCollectionItem { sku, fields, taxonomies, images[] }
 *
 * The bytes go to the API server, which writes them to blob storage — the
 * browser never holds a storage credential. See lib/media/uploadAsset.
 *
 * Every step is replay-safe on purpose, which is what lets a half-finished
 * upload be retried blindly instead of reconciled:
 *
 *   • uploads are keyed on the content digest — the same bytes always resolve
 *     to the same asset, and the probe means a re-shot card costs a round trip
 *     rather than 4 MB.
 *   • saveCollectionItem upserts on `card://<sku>`, and the SKU was minted when
 *     the item was queued.
 *
 * Progress is written back into IndexedDB as it happens (assetId per image), so
 * a tab closed mid-drain resumes rather than restarts.
 */

export { sha256Hex };

type Client = ApolloClient<any>;

/** Put one image in the media library and return its asset id. */
async function uploadImage(accountId: string, item: QueuedItem, index: number): Promise<string> {
  const image = item.images[index];
  if (image.assetId) return image.assetId;

  const sha256 = image.sha256 ?? (await sha256Hex(image.blob));

  const uploaded = await uploadAsset(accountId, image.blob, {
    sha256,
    filename: `${item.sku}-${image.role}.jpg`,
    width: image.width,
    height: image.height,
    captureMeta: item.captureMeta ?? null,
  });

  // Persist the win before moving on, so a failure on the NEXT image does not
  // cost this one's transfer.
  image.assetId = uploaded.assetId;
  image.sha256 = sha256;
  await updateItem(item);

  return uploaded.assetId;
}

export interface DrainResult {
  uploaded: number;
  failed: number;
  errors: string[];
}

/** Push one queued item all the way to a saved card. */
export async function uploadItem(client: Client, item: QueuedItem): Promise<void> {
  item.status = 'uploading';
  item.attempts += 1;
  await updateItem(item);

  for (let i = 0; i < item.images.length; i++) {
    await uploadImage(item.accountId, item, i);
  }

  const images = item.images
    .filter((img) => img.assetId)
    .map((img, position) => ({
      assetId: img.assetId as string,
      role: img.role,
      position,
    }));

  await client.mutate({
    mutation: SAVE_COLLECTION_ITEM,
    variables: {
      accountId: item.accountId,
      input: {
        sku: item.sku,
        title: item.title || null,
        fields: item.fields ?? {},
        taxonomies: item.taxonomies ?? {},
        images,
        ocrText: item.ocrText || null,
        publishStatus: item.publishStatus || 'draft',
      },
    },
  });

  // The card is saved and the bytes are in blob storage — the local copy has
  // no further job, and holding several gigabytes of JPEG hostage in IndexedDB
  // for no reason is how a capture session runs out of quota.
  await removeItem(item.id);
}

/**
 * Try every pending item once, oldest first.
 *
 * Sequential rather than parallel: these are multi-megabyte originals, often
 * over a phone connection, and eight simultaneous PUTs on a bad link finish
 * slower than eight consecutive ones — and fail messier.
 */
export async function drainQueue(
  client: Client,
  accountId: string,
  onProgress?: (item: QueuedItem, index: number, total: number) => void
): Promise<DrainResult> {
  const queue = (await listQueue(accountId)).filter((i) => i.status !== 'done');
  const result: DrainResult = { uploaded: 0, failed: 0, errors: [] };

  for (let i = 0; i < queue.length; i++) {
    const item = queue[i];
    onProgress?.(item, i, queue.length);
    try {
      await uploadItem(client, item);
      result.uploaded += 1;
    } catch (err) {
      result.failed += 1;
      const message = err instanceof Error ? err.message : String(err);
      result.errors.push(`${item.sku}: ${message}`);
      item.status = 'error';
      item.lastError = message;
      await updateItem(item);
    }
  }

  return result;
}
